import { useEffect } from "react";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Link } from "react-router-dom";
import {
  LightBulbIcon,
  EyeIcon,
  UserGroupIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { BlueprintLinesSVG, SectionDividerSVG } from "@/components/SVGElements";

const aboutItems = [
  {
    icon: LightBulbIcon,
    title: "Our Mission",
    description:
      "Delivering comprehensive solutions for civil, structural, and architectural excellence under one roof. We strive to exceed expectations through innovation and precision.",
    color: "bg-orange-500",
  },
  {
    icon: UserGroupIcon,
    title: "Our Team",
    description:
      "Our team comprises experienced civil engineers, skilled structural designers, and innovative architects dedicated to providing integrated solutions that ensure project success.",
    color: "bg-emerald-500",
  },
  {
    icon: EyeIcon,
    title: "Our Vision",
    description:
      "To be the leading provider of innovative, integrated solutions in civil, structural, and architectural design, shaping a sustainable built environment for future generations.",
    color: "bg-slate-900",
  },
];

const values = [
  "HUDA Registered Consultants",
  "ISO Certified Quality Standards",
  "24/7 Project Support",
  "Sustainable Design Practices",
  "Client-Centric Approach",
  "Timely Project Delivery",
];

const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen bg-zinc-50"
    >
      <Navbar />
      <main className="pt-20">
        {/* Page Header */}
        <section className="py-20 bg-slate-900 relative overflow-hidden">
          <div className="absolute inset-0 text-white opacity-10">
            <BlueprintLinesSVG />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                About Us
              </span>
              <h1 className="font-oswald text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4">
                Building Excellence Through{" "}
                <span className="text-orange-500">Innovation</span>
              </h1>
              <p className="text-slate-300 mt-6 text-lg font-manrope max-w-3xl mx-auto">
                Visioner Consultants Pvt Ltd is a premier consultancy firm offering
                integrated Civil, Structural, and Architectural services since 2012.
              </p>
            </motion.div>
          </div>
        </section>

        {/* About Cards */}
        <section className="py-20 bg-white relative">
          <div className="absolute inset-0 text-slate-900">
            <BlueprintLinesSVG />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="grid md:grid-cols-3 gap-8">
              {aboutItems.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="bg-zinc-50 border border-slate-100 p-8 hover:shadow-xl transition-all duration-300"
                >
                  <div
                    className={`w-14 h-14 ${item.color} flex items-center justify-center mb-6`}
                  >
                    <item.icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-oswald text-xl font-semibold text-slate-900 mb-4">
                    {item.title}
                  </h3>
                  <p className="text-slate-600 font-manrope leading-relaxed">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Company Story */}
        <section className="py-20 bg-zinc-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="relative"
              >
                <img
                  src="https://images.unsplash.com/flagged/photo-1576485436509-a7d286952b65?w=800&q=80"
                  alt="Indian office team meeting"
                  className="w-full h-[450px] object-cover"
                />
                <div className="absolute -bottom-6 -right-6 bg-orange-500 text-white p-6 hidden sm:block">
                  <p className="font-oswald text-4xl font-bold">12+</p>
                  <p className="text-sm font-manrope">Years of Excellence</p>
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="space-y-6"
              >
                <span className="text-orange-500 text-sm font-semibold uppercase tracking-wider font-manrope">
                  Our Story
                </span>
                <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900">
                  Committed to Quality & Precision
                </h2>
                <p className="text-slate-600 font-manrope leading-relaxed">
                  With over a decade of experience, we have established ourselves as
                  trusted partners in the construction and consultancy industry. Our
                  commitment to quality, innovative approach, and client-centric
                  solutions set us apart.
                </p>
                <p className="text-slate-600 font-manrope leading-relaxed">
                  We turn blueprints into reality with precision engineering and 
                  sustainable design practices that meet the highest standards of 
                  quality and efficiency.
                </p>
                
                <div className="grid grid-cols-2 gap-4 pt-4">
                  {values.map((value, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <CheckCircleIcon className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700 text-sm font-manrope">{value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="py-4">
          <SectionDividerSVG className="w-full h-5" />
        </div>

        {/* CTA */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-oswald text-3xl sm:text-4xl font-bold text-slate-900 mb-6">
              Want to Work With Us?
            </h2>
            <p className="text-slate-600 font-manrope mb-8">
              Let's discuss your project requirements and how we can help bring your vision to life.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-slate-900 text-white px-8 py-4 font-manrope font-semibold uppercase tracking-wider text-sm hover:bg-slate-800 transition-colors"
              data-testid="about-cta"
            >
              Contact Us
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </motion.div>
  );
};

export default AboutPage;
